import { useEffect, useMemo, useState } from "react";
import { MapContainer, TileLayer, Marker, Popup, Polyline } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { Link } from "react-router-dom";
import clsx from "clsx";
import { Building2, Crosshair, MapPin, Milestone, Route as RouteIcon, Wand2, X } from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import { useToast } from "../../context/ToastContext";
import { getErrorMessage } from "../../api/client";
import * as scheduleApi from "../../api/schedule";
import * as anagraficaApi from "../../api/anagrafica";
import { CentroAssistenza } from "../../api/anagrafica";
import * as routingApi from "../../api/routing";
import * as mappaApi from "../../api/mappa";
import { PuntoMappa, TipoPuntoMappa } from "../../api/mappa";
import { InstallationSchedule, TipoDispositivo } from "../../types";
import { coloreStato, STATO_ORDER } from "../../utils/statoColors";
import { SECTION_COLORS, SectionKey } from "../../utils/sectionColors";
import { Badge } from "../../components/ui/Badge";
import { Button } from "../../components/ui/Button";
import { Input } from "../../components/ui/Input";
import { SlideOver } from "../../components/ui/SlideOver";
// Il pin dei livelli ereditati sta in un modulo condiviso: lo usa anche la
// mappa Wink On Cash di Statistiche, e il trattamento dei punti approssimati
// non deve poter divergere fra le due.
import { buildPuntoIcon } from "../../components/ui/mapIcons";
import { CorniceMappa } from "../../components/ui/CorniceMappa";
import { etichettaIpotesiData } from "../../utils/dataIpotesi";

const DEFAULT_CENTER: [number, number] = [42.5, 12.5]; // centro Italia
const DEFAULT_ZOOM = 5.5;
const TIPI_DISPOSITIVO: TipoDispositivo[] = ["Cassaforte", "Rendiresto", "TVCC", "Allarme", "Altro"];

/**
 * I livelli della mappa, nell'ordine in cui compaiono fra i chip. Il colore
 * viene da sectionColors, la stessa fonte delle intestazioni di sezione: un
 * pin arancione e la pagina Installazioni si riconoscono come la stessa cosa.
 *
 * Le installazioni stanno in questo elenco per il chip e la legenda, ma i loro
 * pin restano colorati per STATO come prima — vedi il commento sul rendering.
 */
const LIVELLI: { tipo: TipoPuntoMappa; etichetta: string; sezione: SectionKey }[] = [
  { tipo: "installazione", etichetta: "Installazioni", sezione: "installazioni" },
  { tipo: "sopralluogo", etichetta: "Sopralluoghi", sezione: "sopralluoghi" },
  { tipo: "intervento", etichetta: "Interventi", sezione: "interventi" },
  { tipo: "attivazione", etichetta: "Attivazioni", sezione: "attivazioni" },
];

/** Livelli caricati da /api/mappa: le installazioni arrivano già da /api/schedule. */
const LIVELLI_EREDITATI: TipoPuntoMappa[] = ["sopralluogo", "intervento", "attivazione"];

const COLORE_LIVELLO: Record<TipoPuntoMappa, string> = {
  installazione: SECTION_COLORS.installazioni.base,
  sopralluogo: SECTION_COLORS.sopralluoghi.base,
  intervento: SECTION_COLORS.interventi.base,
  attivazione: SECTION_COLORS.attivazioni.base,
};

/** Dove porta il "vai a" del popup, per ciascun livello. */
const ROTTA_LIVELLO: Record<TipoPuntoMappa, string> = {
  installazione: "/schedule",
  sopralluogo: "/sopralluoghi",
  intervento: "/interventions",
  attivazione: "/activations",
};

type MapType = "stradale" | "satellite";

// Due provider gratuiti, nessuna chiave API né fatturazione — a differenza
// delle tile ufficiali di Google Maps, che richiederebbero un account Google
// Cloud con carta di credito collegata. Esri World Imagery è lo stesso tipo
// di soluzione "satellite gratuita" usata da moltissime app di mappe.
const TILE_LAYERS: Record<MapType, { url: string; attribution: string }> = {
  stradale: {
    url: "https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png",
    attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
  },
  satellite: {
    url: "https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}",
    attribution: "Tiles &copy; Esri — Source: Esri, Maxar, Earthstar Geographics, and the GIS User Community",
  },
};

// Leaflet richiede icone come asset statici; con Vite l'import di default
// dei marker png è fragile in build. Usiamo un divIcon SVG inline, colorato
// in base allo stato — nessun asset esterno da gestire. `stopNumber`, se
// presente, sostituisce il pallino centrale con il numero di tappa del
// percorso — usato in modalità "pianifica percorso" per mostrare subito
// sulla mappa in che ordine visitare i punti selezionati.
function buildStatoIcon(stato: string, stopNumber?: number): L.DivIcon {
  const color = coloreStato(stato) ?? "#6b7280";
  const center = stopNumber
    ? `<circle cx="13" cy="13" r="8" fill="white"/><text x="13" y="17" font-size="11" font-weight="700" text-anchor="middle" font-family="sans-serif" fill="${color}">${stopNumber}</text>`
    : `<circle cx="13" cy="13" r="5" fill="white"/>`;
  return L.divIcon({
    className: "",
    html: `<svg width="26" height="34" viewBox="0 0 26 34" xmlns="http://www.w3.org/2000/svg">
      <path d="M13 0C5.8 0 0 5.8 0 13c0 9.5 13 21 13 21s13-11.5 13-21C26 5.8 20.2 0 13 0z" fill="${color}" stroke="white" stroke-width="1.5"/>
      ${center}
    </svg>`,
    iconSize: [26, 34],
    iconAnchor: [13, 34],
    popupAnchor: [0, -30],
  });
}

// Icona distinta (quadrata, colore primary) per il Centro Assistenza scelto
// come punto di partenza del percorso — va distinta a colpo d'occhio dai
// pin a goccia delle installazioni.
function buildCentroIcon(): L.DivIcon {
  return L.divIcon({
    className: "",
    html: `<svg width="30" height="30" viewBox="0 0 30 30" xmlns="http://www.w3.org/2000/svg">
      <rect x="1" y="1" width="28" height="28" rx="6" fill="#18273d" stroke="white" stroke-width="2"/>
      <path d="M9 21V12l6-4 6 4v9" stroke="white" stroke-width="1.8" fill="none" stroke-linejoin="round"/>
      <rect x="13" y="15" width="4" height="6" fill="white"/>
    </svg>`,
    iconSize: [30, 30],
    iconAnchor: [15, 15],
    popupAnchor: [0, -15],
  });
}

export default function MapPage() {
  const { user, hasPermission } = useAuth();
  /** Stesso permesso che protegge la rotta /sedi/:id — vedi il popup dei livelli. */
  const puoVedereSedi = hasPermission("clienti_sedi:read");
  const { showToast } = useToast();

  const [records, setRecords] = useState<InstallationSchedule[]>([]);
  /** Sopralluoghi, interventi e attivazioni: posizionati dalla sede collegata. */
  const [punti, setPunti] = useState<PuntoMappa[]>([]);
  /** Livelli accesi. Tutti di default: la mappa multi-livello serve proprio a vederli insieme. */
  const [livelliOn, setLivelliOn] = useState<Set<TipoPuntoMappa>>(
    () => new Set(LIVELLI.map((l) => l.tipo))
  );
  /** I centri sono un livello a sé: sedi operative, non attività da svolgere. */
  const [mostraCentri, setMostraCentri] = useState(true);
  const [isLoading, setIsLoading] = useState(true);
  const [geocodingIds, setGeocodingIds] = useState<Set<string>>(new Set());
  const [selected, setSelected] = useState<InstallationSchedule | null>(null);
  const [panelOpen, setPanelOpen] = useState(false);
  const [mapType, setMapType] = useState<MapType>("stradale");

  // --- Pianificazione percorso -------------------------------------------
  // Stessi ruoli abilitati lato backend su /api/routing (vedi ROUTING_ROLES).
  const canPlanRoute =
    user?.role === "admin" ||
    user?.role === "staff" ||
    user?.role === "centro_assistenza" ||
    user?.role === "operatore_tecnico";
  const [centri, setCentri] = useState<CentroAssistenza[]>([]);
  const [routePanelOpen, setRoutePanelOpen] = useState(false);
  const [routeCentroId, setRouteCentroId] = useState("");
  const [routeStopIds, setRouteStopIds] = useState<string[]>([]);
  const [routeRoundtrip, setRouteRoundtrip] = useState(false);
  const [routeResult, setRouteResult] = useState<{
    geometry: [number, number][];
    distance_km: number;
    duration_min: number;
    stopOrder: string[]; // id delle tappe (escluso il centro) nell'ordine finale di visita
  } | null>(null);
  const [isCalculatingRoute, setIsCalculatingRoute] = useState(false);
  const [isGeocodingCentro, setIsGeocodingCentro] = useState(false);
  const [manualCentroLat, setManualCentroLat] = useState("");
  const [manualCentroLng, setManualCentroLng] = useState("");

  useEffect(() => {
    if (!canPlanRoute) return;
    anagraficaApi.listCentriAssistenza().then(({ centri: list }) => setCentri(list));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canPlanRoute]);

  const routeCentro = centri.find((c) => c.id === routeCentroId) ?? null;

  // Filtri: "Cliente" nel modello dati attuale corrisponde a `insegna`
  // (installation_schedule non ha un concetto di cliente/anagrafica proprio —
  // quello vive nella tabella `clienti`, non ancora collegata a questo modulo).
  const [search, setSearch] = useState("");
  const [statoFilter, setStatoFilter] = useState("");
  const [tipoFilter, setTipoFilter] = useState("");
  const [provinciaFilter, setProvinciaFilter] = useState("");

  async function loadRecords() {
    setIsLoading(true);
    try {
      // Le installazioni continuano ad arrivare da /api/schedule perché su
      // questa pagina servono INTERE: geocodifica su richiesta, selezione come
      // tappa del percorso, elenco "da geocodificare". /api/mappa restituisce
      // solo quello che serve a disegnare un punto, e per gli altri tre livelli
      // è tutto ciò che serve.
      const [{ records: list }, { punti: extra }] = await Promise.all([
        scheduleApi.listSchedule(),
        mappaApi.listPuntiMappa(LIVELLI_EREDITATI),
      ]);
      setRecords(list);
      setPunti(extra);
    } catch (err) {
      showToast(getErrorMessage(err, "Impossibile caricare la mappa."), "error");
    } finally {
      setIsLoading(false);
    }
  }

  function toggleLivello(tipo: TipoPuntoMappa) {
    setLivelliOn((prev) => {
      const next = new Set(prev);
      if (next.has(tipo)) next.delete(tipo);
      else next.add(tipo);
      return next;
    });
  }

  useEffect(() => {
    loadRecords();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /** Sigla provincia confrontabile: maiuscola e senza spazi, "" se assente. */
  function siglaProvincia(v: string | null | undefined): string {
    return (v ?? "").trim().toUpperCase();
  }

  /**
   * Province presenti nei dati caricati, non l'elenco delle 110 italiane: un
   * menu quasi tutto vuoto costringerebbe a cercare le poche voci utili.
   */
  const provinceDisponibili = useMemo(() => {
    const sigle = new Set<string>();
    for (const r of records) {
      const p = siglaProvincia(r.provincia);
      if (p) sigle.add(p);
    }
    // Anche quelle dei livelli ereditati, che arrivano dalla provincia della
    // sede: altrimenti il menu offrirebbe solo le province delle installazioni
    // e filtrare per una provincia dove esistono solo interventi sarebbe
    // impossibile.
    for (const p of punti) {
      const s = siglaProvincia(p.provincia);
      if (s) sigle.add(s);
    }
    return [...sigle].sort();
  }, [records, punti]);

  const filtered = useMemo(() => {
    return records.filter((r) => {
      if (search && !(r.insegna ?? "").toLowerCase().includes(search.toLowerCase())) return false;
      if (statoFilter && r.stato !== statoFilter) return false;
      if (tipoFilter && r.tipo_dispositivo !== tipoFilter) return false;
      // Un record SENZA provincia non viene nascosto: non sapere dove si trova
      // non è una prova che sia altrove, e sparirebbe da ogni filtro senza che
      // nessuno se ne accorga. Si escludono solo quelli collocati altrove.
      if (provinciaFilter) {
        const p = siglaProvincia(r.provincia);
        if (p && p !== provinciaFilter) return false;
      }
      return true;
    });
  }, [records, search, statoFilter, tipoFilter, provinciaFilter]);

  /**
   * Il filtro vale anche per i centri, con la stessa regola: si escludono solo
   * quelli collocati altrove.
   *
   * Oggi in archivio `centri_assistenza.provincia` è NULL su tutti — hanno
   * `comune` ma non la sigla — quindi in pratica l'elenco resta invariato.
   * Dedurre la provincia dal comune sarebbe indovinare; il filtro è già
   * pronto e comincerà a discriminare da solo quando il campo verrà popolato.
   */
  const centriFiltrati = useMemo(() => {
    if (!provinciaFilter) return centri;
    return centri.filter((c) => {
      const p = siglaProvincia(c.provincia);
      return !p || p === provinciaFilter;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [centri, provinciaFilter]);

  /**
   * Livelli ereditati, filtrati.
   *
   * Ricevono la ricerca testuale, il filtro provincia e l'interruttore del
   * proprio livello. NON ricevono il filtro stato né quello dispositivo: sono
   * elenchi costruiti sulle installazioni (gli stati di `STATO_ORDER`, i tipi
   * di dispositivo di una scheda), e applicarli a un intervento o a un
   * sopralluogo li farebbe sparire tutti al primo uso — non perché non
   * corrispondano, ma perché quella domanda non è posta nella loro lingua.
   */
  const puntiFiltrati = useMemo(() => {
    return punti.filter((p) => {
      if (!livelliOn.has(p.tipo)) return false;
      if (search && !`${p.titolo} ${p.cliente ?? ""}`.toLowerCase().includes(search.toLowerCase())) return false;
      if (provinciaFilter) {
        const sigla = siglaProvincia(p.provincia);
        // Stessa regola delle installazioni: chi non dichiara la provincia non
        // viene nascosto, perché non saperlo non è una prova che sia altrove.
        if (sigla && sigla !== provinciaFilter) return false;
      }
      return true;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [punti, livelliOn, search, provinciaFilter]);

  const geocoded = useMemo(
    () => (livelliOn.has("installazione") ? filtered.filter((r) => r.lat !== null && r.lng !== null) : []),
    [filtered, livelliOn]
  );
  const nonGeocoded = useMemo(() => filtered.filter((r) => r.lat === null || r.lng === null), [filtered]);

  const canGeocode = user?.role === "admin" || user?.role === "centro_assistenza";

  async function handleGeocode(record: InstallationSchedule) {
    setGeocodingIds((prev) => new Set(prev).add(record.id));
    try {
      await scheduleApi.geocodeSchedule(record.id);
      showToast(`"${record.insegna ?? "Record"}" geocodificato.`, "success");
      loadRecords();
    } catch (err) {
      showToast(getErrorMessage(err, "Geocoding non riuscito."), "error");
    } finally {
      setGeocodingIds((prev) => {
        const next = new Set(prev);
        next.delete(record.id);
        return next;
      });
    }
  }

  function toggleRouteStop(record: InstallationSchedule) {
    setRouteResult(null); // una selezione diversa invalida il percorso già calcolato
    setRouteStopIds((prev) =>
      prev.includes(record.id) ? prev.filter((id) => id !== record.id) : [...prev, record.id]
    );
  }

  async function handleGeocodeCentro() {
    if (!routeCentro) return;
    setIsGeocodingCentro(true);
    try {
      const { centro } = await anagraficaApi.geocodeCentroAssistenza(routeCentro.id);
      setCentri((prev) => prev.map((c) => (c.id === centro.id ? centro : c)));
      showToast(`"${centro.nome}" geolocalizzato.`, "success");
    } catch (err) {
      showToast(getErrorMessage(err, "Geocoding del centro non riuscito."), "error");
    } finally {
      setIsGeocodingCentro(false);
    }
  }

  async function handleSaveManualCentroPosition(latStr: string, lngStr: string) {
    if (!routeCentro) return;
    const latNum = parseFloat(latStr.replace(",", "."));
    const lngNum = parseFloat(lngStr.replace(",", "."));
    if (Number.isNaN(latNum) || Number.isNaN(lngNum)) {
      showToast("Inserisci latitudine e longitudine valide (es. 44.4056, 8.9463).", "error");
      return;
    }
    setIsGeocodingCentro(true);
    try {
      const { centro } = await anagraficaApi.updateCentroAssistenza(routeCentro.id, { lat: latNum, lng: lngNum });
      setCentri((prev) => prev.map((c) => (c.id === centro.id ? centro : c)));
      showToast(`Posizione di "${centro.nome}" impostata manualmente.`, "success");
    } catch (err) {
      showToast(getErrorMessage(err, "Impossibile salvare la posizione."), "error");
    } finally {
      setIsGeocodingCentro(false);
    }
  }

  async function runRouteCalculation(optimize: boolean) {
    if (!routeCentro?.lat || !routeCentro?.lng) {
      showToast("Seleziona (e geolocalizza) un Centro Assistenza da cui partire.", "error");
      return;
    }
    const stops = routeStopIds
      .map((id) => geocoded.find((r) => r.id === id))
      .filter((r): r is InstallationSchedule => Boolean(r));
    if (stops.length === 0) {
      showToast("Seleziona almeno una tappa cliccando i marker sulla mappa.", "error");
      return;
    }

    // Number(...) invece di un cast diretto: Prisma serializza i campi
    // Decimal (lat/lng) come stringhe in JSON — Leaflet li tollera perché li
    // coercizza internamente, ma l'API di routing valida che siano numeri
    // veri, quindi qui la conversione va fatta esplicitamente.
    const waypoints = [
      { lat: Number(routeCentro.lat), lng: Number(routeCentro.lng) },
      ...stops.map((s) => ({ lat: Number(s.lat), lng: Number(s.lng) })),
    ];

    setIsCalculatingRoute(true);
    try {
      if (optimize) {
        const result = await routingApi.calculateTrip(waypoints, routeRoundtrip);
        // order[0] è sempre il centro (source=first) — il resto, in ordine,
        // sono gli indici (1-based nell'array waypoints) delle tappe.
        const stopOrder = result.order.filter((i) => i !== 0).map((i) => stops[i - 1].id);
        setRouteResult({
          geometry: result.geometry,
          distance_km: result.distance_km,
          duration_min: result.duration_min,
          stopOrder,
        });
      } else {
        const result = await routingApi.calculateRoute(waypoints);
        setRouteResult({
          geometry: result.geometry,
          distance_km: result.distance_km,
          duration_min: result.duration_min,
          stopOrder: stops.map((s) => s.id),
        });
      }
    } catch (err) {
      showToast(getErrorMessage(err, "Impossibile calcolare il percorso."), "error");
    } finally {
      setIsCalculatingRoute(false);
    }
  }

  function handleClearRoute() {
    setRouteStopIds([]);
    setRouteResult(null);
  }

  function handleCloseRoutePanel() {
    setRoutePanelOpen(false);
    handleClearRoute();
  }

  return (
    <div>
      <header className="mb-4 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-ink">Mappa</h1>
          <p className="mt-1 text-sm text-ink/50">
            {isLoading ? "Caricamento…" : `${geocoded.length} installazioni geolocalizzate`}
            {!isLoading && nonGeocoded.length > 0 && ` · ${nonGeocoded.length} senza coordinate`}
          </p>
        </div>
        {canPlanRoute && (
          <Button
            variant={routePanelOpen ? "primary" : "secondary"}
            onClick={() => (routePanelOpen ? handleCloseRoutePanel() : setRoutePanelOpen(true))}
          >
            <RouteIcon className="h-4 w-4" aria-hidden="true" />
            {routePanelOpen ? "Chiudi pianificazione" : "Pianifica percorso"}
          </Button>
        )}
      </header>

      {/* --- Pianificazione percorso --------------------------------------------- */}
      {routePanelOpen && (
        <div className="mb-4 flex flex-col gap-4 rounded-card border border-dashed border-accent/40 bg-accent/5 p-4">
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div className="flex flex-col gap-1.5">
              <label className="text-sm font-medium text-ink">Centro Assistenza di partenza</label>
              <select
                value={routeCentroId}
                onChange={(e) => {
                  setRouteCentroId(e.target.value);
                  setRouteResult(null);
                }}
                className="h-10 rounded-lg border border-line bg-white px-3 text-sm text-ink"
              >
                <option value="">— Seleziona —</option>
                {centriFiltrati.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.nome}
                  </option>
                ))}
              </select>
              {routeCentro && (!routeCentro.lat || !routeCentro.lng) && (
                <div className="mt-1 flex flex-col gap-2 rounded-lg bg-white px-3 py-2 text-xs text-ink/60">
                  <div className="flex items-center justify-between gap-2">
                    Questo centro non è geolocalizzato.
                    <Button size="sm" variant="secondary" loading={isGeocodingCentro} onClick={handleGeocodeCentro}>
                      <Crosshair className="h-3.5 w-3.5" aria-hidden="true" />
                      Geocodifica
                    </Button>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <input
                      type="text"
                      placeholder="Lat"
                      value={manualCentroLat}
                      onChange={(e) => setManualCentroLat(e.target.value)}
                      className="h-8 w-20 rounded border border-line px-2 text-xs"
                    />
                    <input
                      type="text"
                      placeholder="Lng"
                      value={manualCentroLng}
                      onChange={(e) => setManualCentroLng(e.target.value)}
                      className="h-8 w-20 rounded border border-line px-2 text-xs"
                    />
                    <Button
                      size="sm"
                      variant="ghost"
                      loading={isGeocodingCentro}
                      disabled={!manualCentroLat || !manualCentroLng}
                      onClick={() => handleSaveManualCentroPosition(manualCentroLat, manualCentroLng)}
                    >
                      Salva posizione manuale
                    </Button>
                  </div>
                </div>
              )}
            </div>

            <label className="flex items-center gap-2 self-end rounded-lg border border-line bg-white px-3 py-2.5 text-sm text-ink">
              <input
                type="checkbox"
                className="h-4 w-4 rounded border-black/20"
                checked={routeRoundtrip}
                onChange={(e) => {
                  setRouteRoundtrip(e.target.checked);
                  setRouteResult(null);
                }}
              />
              Andata e ritorno al centro
            </label>
          </div>

          <div>
            <p className="mb-2 text-sm font-medium text-ink">
              Tappe selezionate ({routeStopIds.length})
              <span className="ml-2 font-normal text-ink/50">— clicca i marker sulla mappa per aggiungerle</span>
            </p>
            {routeStopIds.length === 0 ? (
              <p className="text-xs text-ink/40">Nessuna tappa selezionata.</p>
            ) : (
              <ul className="flex flex-wrap gap-2">
                {(routeResult?.stopOrder ?? routeStopIds).map((id, i) => {
                  const stop = geocoded.find((r) => r.id === id);
                  if (!stop) return null;
                  return (
                    <li
                      key={id}
                      className="flex items-center gap-1.5 rounded-full border border-line bg-white py-1 pl-3 pr-1.5 text-xs text-ink"
                    >
                      <span className="font-bold text-accent-dark">{i + 1}.</span>
                      {stop.insegna ?? "Senza insegna"}
                      <button
                        type="button"
                        onClick={() => toggleRouteStop(stop)}
                        aria-label="Rimuovi dal percorso"
                        className="rounded-full p-0.5 text-ink/40 hover:bg-black/5 hover:text-ink"
                      >
                        <X className="h-3 w-3" aria-hidden="true" />
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>

          <div className="flex flex-wrap gap-2">
            <Button
              variant="secondary"
              loading={isCalculatingRoute}
              disabled={routeStopIds.length === 0}
              onClick={() => runRouteCalculation(false)}
            >
              <Milestone className="h-4 w-4" aria-hidden="true" />
              Calcola percorso nell'ordine scelto
            </Button>
            <Button
              loading={isCalculatingRoute}
              disabled={routeStopIds.length === 0}
              onClick={() => runRouteCalculation(true)}
            >
              <Wand2 className="h-4 w-4" aria-hidden="true" />
              Calcola il giro migliore
            </Button>
            {routeStopIds.length > 0 && (
              <Button variant="ghost" onClick={handleClearRoute}>
                Svuota tappe
              </Button>
            )}
          </div>

          {routeResult && (
            <div className="rounded-lg bg-white px-4 py-3 text-sm text-ink">
              <span className="font-semibold">{routeResult.distance_km.toFixed(1)} km</span>
              <span className="text-ink/50"> · </span>
              <span className="font-semibold">
                {routeResult.duration_min < 60
                  ? `${Math.round(routeResult.duration_min)} min`
                  : `${Math.floor(routeResult.duration_min / 60)} h ${Math.round(routeResult.duration_min % 60)} min`}
              </span>
              <span className="text-ink/50"> di guida stimati.</span>
            </div>
          )}
        </div>
      )}

      {/* --- Filtri ----------------------------------------------------------- */}
      <div className="mb-4 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <Input
          placeholder="Cerca per insegna / cliente…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />

        <select
          value={statoFilter}
          onChange={(e) => setStatoFilter(e.target.value)}
          className="h-10 rounded-lg border border-line bg-white px-3 text-sm text-ink"
        >
          <option value="">Tutti gli stati</option>
          {STATO_ORDER.map((s) => (
            <option key={s} value={s}>
              {s}
            </option>
          ))}
        </select>

        <select
          value={tipoFilter}
          onChange={(e) => setTipoFilter(e.target.value)}
          className="h-10 rounded-lg border border-line bg-white px-3 text-sm text-ink"
        >
          <option value="">Tutti i dispositivi</option>
          {TIPI_DISPOSITIVO.map((t) => (
            <option key={t} value={t}>
              {t}
            </option>
          ))}
        </select>

        {/* Le province elencate sono solo quelle presenti nei record caricati:
            un menu con tutte quelle italiane sarebbe quasi interamente vuoto. */}
        <select
          value={provinciaFilter}
          onChange={(e) => setProvinciaFilter(e.target.value)}
          className="h-10 rounded-lg border border-line bg-white px-3 text-sm text-ink"
          aria-label="Filtra per provincia"
        >
          <option value="">Tutte le province</option>
          {provinceDisponibili.map((p) => (
            <option key={p} value={p}>
              {p}
            </option>
          ))}
        </select>
      </div>

      {/* --- Livelli: chip di accensione + legenda ------------------------------
          Stesso pattern del Calendario unificato. I chip sono anche la legenda
          dei colori: una legenda separata ripeterebbe le stesse quattro voci. */}
      <div className="mb-3 flex flex-wrap items-center gap-2">
        {LIVELLI.map((l) => {
          const acceso = livelliOn.has(l.tipo);
          const colore = COLORE_LIVELLO[l.tipo];
          const quanti =
            l.tipo === "installazione"
              ? geocoded.length
              : puntiFiltrati.filter((p) => p.tipo === l.tipo).length;
          return (
            <button
              key={l.tipo}
              type="button"
              onClick={() => toggleLivello(l.tipo)}
              aria-pressed={acceso}
              className={clsx(
                "flex items-center gap-2 rounded-full border px-3 py-1.5 text-sm font-medium transition-colors",
                acceso ? "border-transparent text-white" : "border-line bg-white text-ink/45"
              )}
              style={acceso ? { backgroundColor: colore } : undefined}
            >
              <span
                className="h-2.5 w-2.5 rounded-full"
                style={{ backgroundColor: acceso ? "rgba(255,255,255,0.9)" : colore }}
                aria-hidden="true"
              />
              {l.etichetta}
              <span className={clsx("text-xs", acceso ? "text-white/75" : "text-ink/35")}>{quanti}</span>
            </button>
          );
        })}

        {/* Il chip dei centri ha il pallino quadrato, come il loro marker: il
            chip è anche la legenda, e deve dire pure la forma oltre al colore.
            Compare solo se i centri sono stati caricati — l'elenco richiede
            `anagrafica:read`, che non tutti i ruoli hanno. */}
        {centri.length > 0 && (
          <button
            type="button"
            onClick={() => setMostraCentri((v) => !v)}
            aria-pressed={mostraCentri}
            className={clsx(
              "flex items-center gap-2 rounded-full border px-3 py-1.5 text-sm font-medium transition-colors",
              mostraCentri ? "border-transparent bg-primary text-white" : "border-line bg-white text-ink/45"
            )}
          >
            <span
              className="h-2.5 w-2.5 rounded-[3px]"
              style={{ backgroundColor: mostraCentri ? "rgba(255,255,255,0.9)" : "#18273d" }}
              aria-hidden="true"
            />
            Centri Assistenza
            <span className={clsx("text-xs", mostraCentri ? "text-white/75" : "text-ink/35")}>
              {centriFiltrati.filter((c) => c.lat != null && c.lng != null).length}
            </span>
          </button>
        )}
      </div>

      {/* --- Legenda della precisione -------------------------------------------
          Serve solo se in vista c'è almeno un punto approssimato: spiegare una
          distinzione che non si vede è rumore. */}
      {puntiFiltrati.some((p) => p.precisione === "comune") && (
        <div className="mb-4 flex flex-wrap items-center gap-4 text-xs text-ink/60">
          <span className="flex items-center gap-1.5">
            <span className="h-3 w-3 rounded-full bg-ink/70" aria-hidden="true" />
            Posizione all&apos;indirizzo
          </span>
          <span className="flex items-center gap-1.5">
            <span
              className="h-3 w-3 rounded-full border-2 border-dashed border-ink/70"
              aria-hidden="true"
            />
            Posizione indicativa (centro del comune)
          </span>
        </div>
      )}

      {/* --- Legenda degli stati (installazioni) -------------------------------- */}
      <div className="mb-4 flex flex-wrap gap-2">
        {STATO_ORDER.map((stato) => (
          <Badge key={stato} stato={stato}>
            {stato}
          </Badge>
        ))}
      </div>

      {/* --- Mappa -------------------------------------------------------------
          Stessa cornice della mappa Wink On Cash in Statistiche: due mappe nella
          stessa app non devono avere due trattamenti diversi. Il selettore
          stradale/satellite passa nello slot `sopra`, che sta FUORI dal riquadro
          che ritaglia — dentro, la sua ombra verrebbe tagliata dall'angolo. */}
      <CorniceMappa
        style={{ height: "60vh" }}
        sopra={
          // z-index più alto dei pannelli interni di Leaflet (max nativo ~650).
          <div className="absolute right-3 top-3 z-[1000] flex overflow-hidden rounded-lg border border-line bg-white shadow-card">
            {(["stradale", "satellite"] as const).map((tipo) => (
              <button
                key={tipo}
                type="button"
                onClick={() => setMapType(tipo)}
                className={clsx(
                  "px-3 py-1.5 text-xs font-semibold capitalize transition-colors duration-150",
                  mapType === tipo ? "bg-primary text-white" : "bg-white text-ink/60 hover:bg-primary/5"
                )}
              >
                {tipo}
              </button>
            ))}
          </div>
        }
      >
        <MapContainer
          center={DEFAULT_CENTER}
          zoom={DEFAULT_ZOOM}
          scrollWheelZoom
          style={{ height: "100%", width: "100%" }}
        >
          <TileLayer key={mapType} attribution={TILE_LAYERS[mapType].attribution} url={TILE_LAYERS[mapType].url} />

          {routeCentro?.lat && routeCentro?.lng && (
            <Marker position={[routeCentro.lat, routeCentro.lng]} icon={buildCentroIcon()}>
              <Popup>
                <p className="text-sm font-semibold text-ink">{routeCentro.nome}</p>
                <p className="text-xs text-ink/60">Punto di partenza del percorso</p>
              </Popup>
            </Marker>
          )}

          {/* --- Centri Assistenza -------------------------------------------
              Forma diversa dagli altri livelli, non solo colore diverso: un
              centro non è un'attività da svolgere in un posto, è il posto da
              cui si parte. Il quadrato con l'edificio esisteva già per il
              punto di partenza del percorso — è la stessa entità, quindi lo
              stesso segno.

              Il centro scelto come partenza resta escluso perché è già
              disegnato sopra, col suo popup che ne spiega il ruolo. */}
          {mostraCentri &&
            centriFiltrati
              .filter((c) => c.id !== routeCentroId && c.lat != null && c.lng != null)
              .map((c) => (
                <Marker key={`centro-${c.id}`} position={[Number(c.lat), Number(c.lng)]} icon={buildCentroIcon()}>
                  <Popup>
                    <div className="text-sm">
                      <p className="text-xs font-semibold uppercase tracking-wide text-ink/50">Centro Assistenza</p>
                      <p className="mt-0.5 font-semibold text-ink">{c.nome}</p>
                      {c.comune && <p className="text-ink/60">{c.comune}</p>}
                    </div>
                  </Popup>
                </Marker>
              ))}

          {routeResult && <Polyline positions={routeResult.geometry} pathOptions={{ color: "#18273d", weight: 4, opacity: 0.75 }} />}

          {geocoded.map((r) => {
            const displayOrder = routeResult?.stopOrder ?? routeStopIds;
            const stopIndex = displayOrder.indexOf(r.id);
            const stopNumber = stopIndex >= 0 ? stopIndex + 1 : undefined;

            return (
              <Marker
                key={r.id}
                position={[r.lat as number, r.lng as number]}
                icon={buildStatoIcon(r.stato, stopNumber)}
                eventHandlers={routePanelOpen ? { click: () => toggleRouteStop(r) } : undefined}
              >
                {!routePanelOpen && (
                  <Popup>
                    <div className="text-sm">
                      <p className="font-semibold text-ink">{r.insegna ?? "Senza insegna"}</p>
                      <p className="text-ink/60">{r.localita ?? "—"}</p>
                      {r.tipo_dispositivo && <p className="text-ink/60">{r.tipo_dispositivo}</p>}
                      <p className="mt-1">
                        <Badge stato={r.stato}>{r.stato}</Badge>
                      </p>
                      {r.ipotesi_data && (
                        <p className="mt-1 text-xs text-ink/50">
                          Prevista: {etichettaIpotesiData(r.ipotesi_data, r.stato)}
                        </p>
                      )}
                      <Link to="/schedule" className="mt-2 inline-block text-xs font-semibold text-primary hover:underline">
                        Vai al calendario →
                      </Link>
                    </div>
                  </Popup>
                )}
              </Marker>
            );
          })}

          {/* --- Livelli ereditati: sopralluoghi, interventi, attivazioni -------
              In modalità pianificazione percorso restano fuori: le tappe si
              scelgono fra le installazioni, e un click su un pin non
              selezionabile sembrerebbe un guasto. */}
          {!routePanelOpen &&
            puntiFiltrati.map((p) => {
              const approssimato = p.precisione === "comune";
              return (
                <Marker
                  key={`${p.tipo}-${p.id}`}
                  position={[p.lat, p.lng]}
                  icon={buildPuntoIcon(COLORE_LIVELLO[p.tipo], approssimato)}
                >
                  <Popup>
                    <div className="text-sm">
                      <p className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide" style={{ color: COLORE_LIVELLO[p.tipo] }}>
                        <span className="h-2 w-2 rounded-full" style={{ backgroundColor: COLORE_LIVELLO[p.tipo] }} aria-hidden="true" />
                        {LIVELLI.find((l) => l.tipo === p.tipo)?.etichetta ?? p.tipo}
                      </p>
                      <p className="mt-0.5 font-semibold text-ink">{p.titolo}</p>
                      {p.cliente && <p className="text-ink/60">{p.cliente}</p>}
                      {p.sede_nome && <p className="text-ink/60">Sede: {p.sede_nome}</p>}
                      {p.stato && (
                        <p className="mt-1">
                          <Badge stato={p.stato}>{p.stato}</Badge>
                        </p>
                      )}
                      {p.data && (
                        <p className="mt-1 text-xs text-ink/50">
                          {new Date(p.data).toLocaleDateString("it-IT")}
                        </p>
                      )}

                      {/* Detto qui e non solo nella legenda: chi clicca un pin
                          approssimato sta guardando proprio quello, ed è il
                          momento in cui la precisazione serve davvero. */}
                      {approssimato && (
                        <p className="mt-2 rounded bg-amber-50 px-2 py-1 text-xs text-amber-900">
                          Posizione indicativa (centro del comune): l&apos;indirizzo della sede non è
                          stato riconosciuto, il punto non è quello esatto.
                        </p>
                      )}

                      <div className="mt-2 flex flex-col gap-1">
                        {/* La Vista Sede è dietro `clienti_sedi:read`: il link
                            compare solo a chi può aprirla, invece di offrire a
                            tutti una porta che sbatte. */}
                        {p.sede_id && puoVedereSedi && (
                          <Link
                            to={`/sedi/${p.sede_id}`}
                            className="text-xs font-semibold text-primary hover:underline"
                          >
                            Tutto su questa sede →
                          </Link>
                        )}
                        <Link
                          to={ROTTA_LIVELLO[p.tipo]}
                          className="text-xs font-semibold text-primary hover:underline"
                        >
                          Vai a {LIVELLI.find((l) => l.tipo === p.tipo)?.etichetta ?? "elenco"} →
                        </Link>
                      </div>
                    </div>
                  </Popup>
                </Marker>
              );
            })}
        </MapContainer>
      </CorniceMappa>
      {routePanelOpen && (
        <p className="mt-2 text-xs text-ink/50">
          <Building2 className="mr-1 inline h-3.5 w-3.5 align-text-bottom" aria-hidden="true" />
          In modalità pianificazione i marker si selezionano con un click invece di aprire la scheda — chiudi la
          pianificazione per tornare alla consultazione normale.
        </p>
      )}

      {/* --- Da geocodificare ---------------------------------------------------- */}
      {!isLoading && nonGeocoded.length > 0 && (
        <section className="mt-6">
          <h2 className="mb-3 text-sm font-bold uppercase tracking-wide text-ink/50">
            Senza coordinate ({nonGeocoded.length})
          </h2>
          <div className="overflow-hidden rounded-card border border-line bg-white shadow-card">
            {nonGeocoded.map((r, i) => (
              <div
                key={r.id}
                role="button"
                tabIndex={0}
                onClick={() => {
                  setSelected(r);
                  setPanelOpen(true);
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    setSelected(r);
                    setPanelOpen(true);
                  }
                }}
                className={clsx(
                  "flex w-full cursor-pointer items-center justify-between px-4 py-3 text-left text-sm transition-colors duration-150 hover:bg-primary/5",
                  i > 0 && "border-t border-line"
                )}
              >
                <div className="min-w-0">
                  <p className="truncate font-medium text-ink">{r.insegna ?? "Senza insegna"}</p>
                  <p className="truncate text-xs text-ink/50">{r.localita ?? "Nessuna località indicata"}</p>
                </div>
                {canGeocode && (
                  <Button
                    size="sm"
                    variant="secondary"
                    loading={geocodingIds.has(r.id)}
                    disabled={!r.localita}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleGeocode(r);
                    }}
                  >
                    <Crosshair className="h-3.5 w-3.5" aria-hidden="true" />
                    Geocodifica
                  </Button>
                )}
              </div>
            ))}
          </div>
        </section>
      )}

      <SlideOver
        open={panelOpen}
        onClose={() => setPanelOpen(false)}
        title={selected?.insegna ?? "Senza insegna"}
      >
        {selected && (
          <NonGeocodedPanel
            key={selected.id}
            record={selected}
            canEdit={canGeocode}
            onSaved={(updated) => {
              setSelected(updated);
              loadRecords();
            }}
          />
        )}
      </SlideOver>
    </div>
  );
}

// ============================================================================
// Pannello di dettaglio/correzione per un record senza coordinate — permette
// di correggere un indirizzo sbagliato e ritentare il geocoding, oppure, se
// l'indirizzo proprio non viene trovato da Nominatim, di posizionare il pin
// inserendo manualmente latitudine/longitudine.
// ============================================================================
function NonGeocodedPanel({
  record,
  canEdit,
  onSaved,
}: {
  record: InstallationSchedule;
  canEdit: boolean;
  onSaved: (updated: InstallationSchedule) => void;
}) {
  const { showToast } = useToast();
  const [insegna, setInsegna] = useState(record.insegna ?? "");
  const [indirizzo, setIndirizzo] = useState(record.indirizzo ?? "");
  const [localita, setLocalita] = useState(record.localita ?? "");
  const [provincia, setProvincia] = useState(record.provincia ?? "");
  const [lat, setLat] = useState("");
  const [lng, setLng] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [isGeocoding, setIsGeocoding] = useState(false);

  async function handleSaveAndGeocode() {
    setIsSaving(true);
    try {
      await scheduleApi.updateSchedule(record.id, { insegna, indirizzo, localita, provincia });
      setIsGeocoding(true);
      try {
        const { record: geocoded } = await scheduleApi.geocodeSchedule(record.id);
        showToast("Indirizzo aggiornato e geolocalizzato.", "success");
        onSaved(geocoded);
      } catch (err) {
        showToast(
          getErrorMessage(err, "Indirizzo salvato, ma il geocoding automatico non ha trovato una corrispondenza."),
          "error"
        );
      } finally {
        setIsGeocoding(false);
      }
    } catch (err) {
      showToast(getErrorMessage(err, "Impossibile salvare le modifiche."), "error");
    } finally {
      setIsSaving(false);
    }
  }

  async function handleSaveManualPosition() {
    const latNum = parseFloat(lat.replace(",", "."));
    const lngNum = parseFloat(lng.replace(",", "."));
    if (Number.isNaN(latNum) || Number.isNaN(lngNum)) {
      showToast("Inserisci latitudine e longitudine valide (es. 44.4056, 8.9463).", "error");
      return;
    }
    setIsSaving(true);
    try {
      const { record: updated } = await scheduleApi.updateSchedule(record.id, { lat: latNum, lng: lngNum });
      showToast("Posizione impostata manualmente.", "success");
      onSaved(updated);
    } catch (err) {
      showToast(getErrorMessage(err, "Impossibile salvare la posizione."), "error");
    } finally {
      setIsSaving(false);
    }
  }

  return (
    <div className="flex flex-col gap-6">
      <div className="rounded-lg border border-dashed border-accent/40 bg-accent/5 px-4 py-3 text-sm text-ink/70">
        <MapPin className="mb-1 h-4 w-4 text-accent" aria-hidden="true" />
        Questo record non ha coordinate — probabilmente l'indirizzo non è stato trovato dal servizio di geocoding.
        Correggilo qui sotto e ritenta, oppure imposta la posizione manualmente più in basso.
      </div>

      <div className="flex flex-col gap-4">
        <p className="text-sm font-bold uppercase tracking-wide text-ink/50">Indirizzo</p>
        <Input label="Insegna" value={insegna} onChange={(e) => setInsegna(e.target.value)} disabled={!canEdit} />
        <Input
          label="Indirizzo"
          placeholder="Via e civico"
          value={indirizzo}
          onChange={(e) => setIndirizzo(e.target.value)}
          disabled={!canEdit}
        />
        <Input
          label="Località"
          value={localita}
          onChange={(e) => setLocalita(e.target.value)}
          disabled={!canEdit}
          hint="Città/comune — usata insieme a indirizzo e provincia per il geocoding."
        />
        <Input label="Provincia" value={provincia} onChange={(e) => setProvincia(e.target.value)} disabled={!canEdit} />

        {canEdit && (
          <Button onClick={handleSaveAndGeocode} loading={isSaving || isGeocoding} disabled={!localita}>
            <Crosshair className="h-4 w-4" aria-hidden="true" />
            Salva e ritenta geocoding
          </Button>
        )}
      </div>

      {canEdit && (
        <div className="flex flex-col gap-4 border-t border-line pt-4">
          <div>
            <p className="text-sm font-bold uppercase tracking-wide text-ink/50">Posizione manuale</p>
            <p className="mt-1 text-xs text-ink/50">
              Se l'indirizzo non viene trovato automaticamente, inserisci qui le coordinate (es. da Google Maps: tasto
              destro sul punto → copia le coordinate).
            </p>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Input label="Latitudine" placeholder="44.4056" value={lat} onChange={(e) => setLat(e.target.value)} />
            <Input label="Longitudine" placeholder="8.9463" value={lng} onChange={(e) => setLng(e.target.value)} />
          </div>
          <Button variant="secondary" onClick={handleSaveManualPosition} loading={isSaving} disabled={!lat || !lng}>
            <MapPin className="h-4 w-4" aria-hidden="true" />
            Salva posizione
          </Button>
        </div>
      )}

      <Link to="/schedule" className="text-xs font-semibold text-primary hover:underline">
        Vai al calendario per modificare tutti gli altri campi →
      </Link>
    </div>
  );
}
